import {
  closestCorners,
  getFirstCollision,
  KeyboardCode,
  DroppableContainer,
  KeyboardCoordinateGetter,
  DropAnimation,
  DropAnimationSideEffects,
  Modifier,
  UniqueIdentifier,
} from "@dnd-kit/core";
import { subtract, Transform, CSS } from "@dnd-kit/utilities";
import { restrictToParentElement } from "@dnd-kit/modifiers";

import { Column, ColumnId } from "./types";
import { CONTAINERS } from "./useTwoColumnDND";
import { DND_VERTICAL_MARGIN, DROP_ANIMATION_DURATION, SCALING_FACTOR } from "./constants";

const directions: string[] = [
  KeyboardCode.Down,
  KeyboardCode.Right,
  KeyboardCode.Up,
  KeyboardCode.Left,
];

export const getCoordinateGetter =
  (vertical?: boolean, emptyContainerOffset?: number): KeyboardCoordinateGetter =>
  (
    event,
    { context: { active, droppableRects, droppableContainers, collisionRect, scrollableAncestors } }
  ) => {
    if (directions.includes(event.code)) {
      event.preventDefault();

      if (!active || !collisionRect) {
        return;
      }

      const currentCollisions = closestCorners({
        active,
        collisionRect: collisionRect,
        droppableRects,
        droppableContainers: droppableContainers.getEnabled(),
        pointerCoordinates: null,
      });

      const parentSize = droppableContainers
        .toArray()
        .find(
          (container) =>
            CONTAINERS.includes(container.id as ColumnId) &&
            container.data.current?.sortable.items.includes(active.id)
        )?.data.current?.sortable.items.length;

      const currentClosestId = getFirstCollision(currentCollisions, "id");
      const canMoveToOtherContainer = vertical
        ? [KeyboardCode.Up, KeyboardCode.Down].includes(event.code as KeyboardCode)
        : [KeyboardCode.Right, KeyboardCode.Left].includes(event.code as KeyboardCode);

      const droppableIds = new Set(droppableContainers.getEnabled().map(({ id }) => id));

      const filteredContainers: DroppableContainer[] = [];
      droppableContainers.getEnabled().forEach((entry) => {
        if (!entry || entry?.disabled || currentClosestId === entry.id) {
          return;
        }

        const rect = droppableRects.get(entry.id);

        if (!rect) {
          return;
        }

        const data = entry.data.current;

        if (data) {
          const { type, children } = data;
          const hasDroppableChildren = children?.some((child: UniqueIdentifier) =>
            droppableIds.has(child)
          );

          // Items are targeted directly, so the container itself is only worth targeting when none
          // of its children accepts a drop: it is either empty or holds disabled items only
          if (type === "container" && hasDroppableChildren) {
            if (active.data.current?.type !== "container") {
              return;
            }
          }
        }

        switch (event.code) {
          case KeyboardCode.Down:
            if (collisionRect.top < rect.top) {
              filteredContainers.push(entry);
            }
            break;
          case KeyboardCode.Up:
            if (collisionRect.top > rect.top) {
              filteredContainers.push(entry);
            }
            break;
          case KeyboardCode.Left:
            if (collisionRect.left >= rect.left + rect.width) {
              filteredContainers.push(entry);
            }
            break;
          case KeyboardCode.Right:
            if (collisionRect.left + collisionRect.width <= rect.left) {
              filteredContainers.push(entry);
            }
            break;
        }
      });

      const collisions = closestCorners({
        active,
        collisionRect: collisionRect,
        droppableRects,
        droppableContainers: filteredContainers,
        pointerCoordinates: null,
      });

      const closestId = getFirstCollision(collisions, "id");

      if (closestId != null) {
        const activeDroppable = droppableContainers.get(active.id);
        const newDroppable = droppableContainers.get(closestId);
        const newRect = newDroppable ? droppableRects.get(newDroppable.id) : null;
        const newNode = newDroppable == null ? void 0 : newDroppable.node.current;

        if (newNode && newRect && activeDroppable && newDroppable) {
          const hasSameContainer =
            activeDroppable.data.current?.sortable.containerId ===
            newDroppable.data.current?.sortable.containerId;

          if (!canMoveToOtherContainer && !hasSameContainer) {
            return;
          }

          if (!hasSameContainer && !vertical) {
            scrollableAncestors.forEach((node) => (node.scrollTop = 0));
          }

          const rectCoordinates = {
            x: newRect.left,
            y: newRect.top,
          };

          if (!hasSameContainer) {
            // It fixes position of item when we have empty container
            if (CONTAINERS.includes(newDroppable.id as ColumnId)) {
              // Move item to empty container and calculate empty container size
              if (event.code === KeyboardCode.Down && vertical) {
                return {
                  x: newRect.left,
                  y: newRect.top + (emptyContainerOffset ?? 0),
                };
              }

              return {
                x: newRect.left,
                y: newRect.top,
              };
              // Move item down to another container and empty previous container
            } else if (parentSize === 1 && event.code === KeyboardCode.Down) {
              return {
                x: newRect.left,
                y: newRect.top + (emptyContainerOffset ?? 0),
              };
              // Move item up to another container and empty previous container
            } else if (parentSize === 1 && event.code === KeyboardCode.Up) {
              return {
                x: newRect.left,
                y:
                  newRect.top +
                  (active.rect.current?.initial?.height ?? 0) +
                  DND_VERTICAL_MARGIN -
                  1,
              };
              // Recalculate position of item when we move between containers
            } else {
              if (vertical && event.code === KeyboardCode.Down) {
                return {
                  x: newRect.left,
                  y:
                    newRect.top - (active.rect.current?.initial?.height ?? 0) - DND_VERTICAL_MARGIN,
                };
              }

              if (vertical && event.code === KeyboardCode.Up) {
                return {
                  x: newRect.left,
                  y:
                    newRect.top + (active.rect.current?.initial?.height ?? 0) + DND_VERTICAL_MARGIN,
                };
              }
            }
          }

          const isAfterActive =
            activeDroppable.data.current?.sortable.index <
            newDroppable.data.current?.sortable.index;

          const offset = !hasSameContainer
            ? {
                x: 0,
                y: 0,
              }
            : {
                x: isAfterActive ? collisionRect.width - newRect.width : 0,
                y: isAfterActive ? collisionRect.height - newRect.height : 0,
              };

          const newCoordinates =
            offset.x && offset.y ? rectCoordinates : subtract(rectCoordinates, offset);
          return newCoordinates;
        }
      }
    }
    return undefined;
  };

export const sliceHiddenItems = <T extends string, U extends Record<string, unknown> | undefined>(
  columnConfig: Column<T, U>
) =>
  columnConfig.reduce(
    ({ visible, hidden }, item) => {
      if (item.hidden) {
        return {
          hidden: [...hidden, item],
          visible,
        };
      }

      return {
        hidden,
        visible: [...visible, item],
      };
    },
    { visible: [] as Column<T, U>, hidden: [] as Column<T, U> }
  );

const keyframes = ({ transform }: { transform: { initial: Transform; final: Transform } }) => [
  {
    transform: CSS.Transform.toString(transform.initial),
  },
  {
    transform: CSS.Transform.toString({
      ...transform.final,
      scaleX: 1 / SCALING_FACTOR,
      scaleY: 1 / SCALING_FACTOR,
    }),
  },
];

const sideEffects: DropAnimationSideEffects = ({ dragOverlay }) => {
  const child = dragOverlay.node.querySelector("[data-animation]");
  if (child) {
    child.animate(
      [
        {
          boxShadow: "var(--draggable-item-dragged-box-shadow)",
        },
        {
          boxShadow: "none",
        },
      ],
      { duration: DROP_ANIMATION_DURATION, easing: "ease-in-out", fill: "forwards" }
    );
  }
};

export const restrictToParentElementWhenUsingKeyboard: Modifier = (args) => {
  if (args.activatorEvent instanceof KeyboardEvent) {
    return restrictToParentElement(args);
  }

  return args.transform;
};

export const dropAnimation: DropAnimation = {
  keyframes,
  sideEffects,
  duration: DROP_ANIMATION_DURATION,
};
